<template>
  <div
    :key="suggestion.id"
    class="c-notecard text-correctionBlue"
    :class="{
      'is-selected': isSelected,
      'is-solved': isSolved,
      'is-new': isNew,
    }"
    @click="selectIfNew"
  >
    <div class="c-notecard__inner mb-4 pa-3">
      <div v-if="suggestion.correctionType === 'staticCheck'">
        <span
          class="c-notecard__type"
          data-test="type"
        >
          <v-icon
            class="correction-tab__status-icon"
            size="16"
          >mdi-alert-circle-outline</v-icon>
          AI作成が疑われる表現
        </span>
        <div
          class="mt-1 messages"
          data-test="answer"
        >
          {{ suggestion.quote }}
        </div>
        <div
          class="messages messages__reason"
          data-test="reason"
        >
          理由：{{ suggestion.reason }}
        </div>
        <div class="d-flex mt-2">
          <v-btn
            v-if="isStaticCheckFixable && isNew"
            outlined
            text
            color="#D72E4B"
            class="ma-0 mr-2 font-weight-bold"
            data-test="accept-btn"
            @click.stop="acceptSuggestion"
          >
            修正
          </v-btn>
          <v-btn
            outlined
            text
            color="correctionBlue"
            class="ma-0 font-weight-bold"
            data-test="ignore-btn"
            @click.stop="ignoreSuggestion"
          >
            問題ありません
          </v-btn>
        </div>
      </div>
      <div v-else-if="suggestion.correctionType === 'misreading'">
        <div>
          <span
            class="c-notecard__type"
            data-test="type"
          >
            <v-icon
              class="correction-tab__status-icon"
              size="16"
            >mdi-alert-circle-outline</v-icon>
            相談文の再確認
          </span>
          <div
            class="mt-1 messages"
            data-test="answer"
          >
            {{ misreadingAnswer }}
          </div>
          <div
            v-if="misreadingReason"
            class="messages messages__reason"
            data-test="reason"
          >
            理由：{{ misreadingReason }}
          </div>
          <v-btn
            v-if="!isSolved"
            outlined
            text
            color="correctionBlue"
            class="ma-0 mt-2 font-weight-bold"
            data-test="ignore-btn"
            @click.stop="ignoreSuggestion"
          >
            問題ありません
          </v-btn>
        </div>
      </div>
      <div v-else-if="suggestion.correctionType === 'typo'">
        <span
          class="c-notecard__type"
          data-test="type"
        >
          <v-icon
            class="correction-tab__status-icon"
            size="16"
          >mdi-alert-circle-outline</v-icon>
          誤字脱字
        </span>

        <div
          class="messages"
          data-test="answer-to-fix"
        >
          <div
            class="mt-1"
            data-test="answer"
          >
            添削前：
            <span
              v-for="(segment, index) in highlightBeforeAfter.answerSegments"
              :key="`answer-${suggestion.id}-${index}`"
              :class="{ highlight: segment.highlighted }"
            >
              {{ segment.text }}
            </span>
          </div>
          <span
            data-test="fix"
            class="d-inline-block"
          >
            添削後：
            <span
              v-for="(segment, index) in highlightBeforeAfter.fixSegments"
              :key="`fix-${suggestion.id}-${index}`"
              :class="{ highlight: segment.highlighted }"
            >
              {{ segment.text }}
            </span>
          </span>
        </div>
        <div
          v-if="isNew"
          class="d-flex mt-2"
        >
          <v-btn
            outlined
            text
            color="#D72E4B"
            class="ma-0 mr-2 font-weight-bold"
            data-test="accept-btn"
            @click.stop="acceptSuggestion"
          >
            修正
          </v-btn>
          <v-btn
            outlined
            text
            color="correctionBlue"
            class="ma-0 mr-2 font-weight-bold"
            style="opacity: 1"
            data-test="ignore-btn"
            @click.stop="ignoreSuggestion"
          >
            問題ありません
          </v-btn>
        </div>
      </div>
      <div
        v-else
        class="unanswered-card"
      >
        <span class="c-notecard__type">
          <v-icon
            class="correction-tab__status-icon"
            size="16"
          >mdi-alert-circle-outline</v-icon>
          疑問への回答不足
        </span>
        <div class="messages unanswered-card__body">
          {{ questionBody }}
        </div>
        <div
          v-if="questionReason"
          class="messages messages__reason unanswered-card__reason"
        >
          理由：{{ questionReason }}
        </div>
        <div class="d-flex mt-2">
          <v-btn
            outlined
            text
            color="correctionBlue"
            class="ma-0 font-weight-bold"
            data-test="follow-up-btn"
            @click.stop="ignoreSuggestion"
          >
            問題ありません
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from "vue";

const buildSegments = (text, startIndex, endIndex) => {
  if (!text) return [];
  if (startIndex > endIndex) {
    return [{ text, highlighted: false }];
  }

  const segments = [];
  const leading = text.slice(0, startIndex);
  if (leading) {
    segments.push({ text: leading, highlighted: false });
  }

  const highlighted = text.slice(startIndex, endIndex + 1);
  if (highlighted) {
    segments.push({ text: highlighted, highlighted: true });
  }

  const trailing = text.slice(endIndex + 1);
  if (trailing) {
    segments.push({ text: trailing, highlighted: false });
  }

  return segments;
};

const diffHighlight = (beforeText, afterText) => {
  let start = 0;
  while (start < beforeText.length && start < afterText.length && beforeText[start] === afterText[start]) {
    start += 1;
  }

  let endBefore = beforeText.length - 1;
  let endAfter = afterText.length - 1;
  while (endBefore >= start && endAfter >= start && beforeText[endBefore] === afterText[endAfter]) {
    endBefore -= 1;
    endAfter -= 1;
  }

  return {
    beforeSegments: buildSegments(beforeText, start, endBefore),
    afterSegments: buildSegments(afterText, start, endAfter),
  };
};

const plainSegments = (text) => (text ? [{ text, highlighted: false }] : []);

export default defineComponent({
  name: "SuggestionCard",
  props: {
    suggestion: {
      type: Object,
      required: true,
    },
    selectedSuggestionId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["select", "accept", "ignore"],
  setup(props, { emit }) {
    const isSelected = computed(() => props.selectedSuggestionId === props.suggestion.id);
    const isSolved = computed(() => props.suggestion.resolution === "changed");
    const isNew = computed(() => props.suggestion.resolution !== "changed");

    const highlightBeforeAfter = computed(() => {
      if (props.suggestion.correctionType !== "typo") {
        return { answerSegments: [], fixSegments: [] };
      }

      const answer = props.suggestion.quote ?? "";
      const fix = props.suggestion.replacement ?? "";
      if (!answer || !fix) {
        return {
          answerSegments: plainSegments(answer),
          fixSegments: plainSegments(fix),
        };
      }

      const result = diffHighlight(answer, fix);
      return {
        answerSegments: result.beforeSegments,
        fixSegments: result.afterSegments,
      };
    });

    const misreadingAnswer = computed(() => props.suggestion.quote ?? props.suggestion.reason ?? "");
    const misreadingReason = computed(() => props.suggestion.reason ?? "");

    const questionBody = computed(() => {
      if (props.suggestion.correctionType !== "unansweredQuestion") return "";
      return props.suggestion.extractedQuestion?.question ?? "回答漏れが検出されています";
    });

    const questionReason = computed(() => {
      if (props.suggestion.correctionType !== "unansweredQuestion") return "";
      return props.suggestion.reason ?? "";
    });

    const selectIfNew = () => {
      if (!isNew.value) return;
      emit("select", props.suggestion.id);
    };

    const acceptSuggestion = () => emit("accept", props.suggestion.id);
    const ignoreSuggestion = () => emit("ignore", props.suggestion.id);

    const isStaticCheckFixable = computed(() => {
      if (props.suggestion.correctionType !== "staticCheck") return false;
      return (
        props.suggestion.ruleType === "asterisk" &&
        props.suggestion.replacement != null
      );
    });

    return {
      isSelected,
      isSolved,
      isNew,
      highlightBeforeAfter,
      misreadingAnswer,
      misreadingReason,
      questionBody,
      questionReason,
      selectIfNew,
      acceptSuggestion,
      ignoreSuggestion,
      isStaticCheckFixable,
    };
  },
});
</script>

<style scoped lang="scss">
.c-notecard {
  &__type {
    display: inline-flex;
    align-items: center;
    height: 21px;
    font-size: 12px;
    padding: 0 6px;
    background: #fee2e2;
    color: #dc2626;
    border-radius: 4px;

    .is-solved & {
      opacity: 0.5;
    }
  }

  .messages {
    font-size: 14px;
    color: #333;

    &__reason {
      color: #A5A5A5;
    }
  }

  :deep(.messages .highlight) {
    color: #D72E4B;
    text-decoration: underline;
  }

  &__inner {
    background-color: white;
    border-radius: 4px;
    border: 1px solid #ccc;
    cursor: pointer;
    transition: height 0.3s linear;

    .is-new.is-selected & {
      background: #ffecef !important;
    }

    .is-solved & {
      border: 1px solid #eee;
    }

    .is-solved & .messages {
      opacity: 0.5;
    }
  }
}

.unanswered-card__body {
  margin-top: 4px;
}

.unanswered-card__reason {
  margin-top: 4px;
}
</style>

<style lang="scss">
.c-notecard {
  transition: transform 0.5s ease;
  will-change: transform;
}
</style>
